import React, { useState } from 'react';
import { FaFileAlt, FaCloudUploadAlt, FaEye, FaTrash, FaCheck, FaCogs, FaDownload, FaCalendarAlt, FaFilePdf, FaInfoCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import usePreviredPeriodo from '../../hooks/usePreviredPeriodo';
import '../../styles/pages/RRHH/PreviredPage.css';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatCLP = (val) => `$${parseInt(val || 0).toLocaleString('es-CL')}`;

const PreviredPage = ({ token, id_company }) => {
    const currentDate = new Date();
    const {
        mes, setMes, anio, setAnio,
        estado, loading, error,
        descargarTxt, descargando,
        subirPdf, subiendo,
        verDetalle, cerrarDetalle, detalle,
        eliminarPdf, cambiarEstadoPdf, reprocesar,
    } = usePreviredPeriodo(id_company, currentDate.getMonth() + 1, currentDate.getFullYear());

    const [pdfFile, setPdfFile] = useState(null);
    const [activeTab, setActiveTab] = useState('AFP');

    const pdf = estado?.pdf_cargado;

    // --- Handlers ---

    const handleDescargarTxt = async () => {
        try {
            Swal.fire({ title: 'Generando...', html: 'Preparando archivo TXT', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await descargarTxt();
            Swal.fire('Descargado', `Archivo previred_${String(mes).padStart(2, '0')}_${anio}.txt generado`, 'success');
        } catch (err) {
            Swal.fire('Error', err.message || 'Error al generar archivo', 'error');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        } else if (file) {
            Swal.fire('Error', 'Solo se permiten archivos PDF', 'error');
            e.target.value = '';
        }
    };

    const handleSubirPdf = async () => {
        if (!pdfFile) return;
        try {
            Swal.fire({ title: 'Procesando PDF...', html: 'Extrayendo datos con IA', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const result = await subirPdf(pdfFile);
            Swal.fire({
                title: 'PDF Procesado',
                html: `
                    <p><strong>Trabajadores:</strong> ${result.data?.resumen?.total_trabajadores || 0}</p>
                    <p><strong>Total Remuneraciones:</strong> ${formatCLP(result.data?.resumen?.total_remuneraciones)}</p>
                    <p><strong>Registros insertados:</strong> ${result.data?.detalles_insertados || 0}</p>
                `,
                icon: 'success'
            });
            setPdfFile(null);
            const input = document.getElementById('pdf-input');
            if (input) input.value = '';
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Error al procesar el PDF', 'error');
        }
    };

    const handleVerDetalle = async (id) => {
        try {
            await verDetalle(id);
            setActiveTab('AFP');
        } catch {
            Swal.fire('Error', 'No se pudo cargar el detalle', 'error');
        }
    };

    const handleEliminar = async (id) => {
        const result = await Swal.fire({
            title: 'Eliminar registro?', text: 'Esta accion no se puede deshacer',
            icon: 'warning', showCancelButton: true, confirmButtonText: 'Si, eliminar', cancelButtonText: 'Cancelar'
        });
        if (result.isConfirmed) {
            try {
                await eliminarPdf(id);
                Swal.fire('Eliminado', 'Registro eliminado correctamente', 'success');
            } catch {
                Swal.fire('Error', 'No se pudo eliminar el registro', 'error');
            }
        }
    };

    const handleReprocesar = async (id) => {
        let mesSig = mes + 1, anioSig = anio;
        if (mesSig > 12) { mesSig = 1; anioSig = anio + 1; }

        const confirm = await Swal.fire({
            title: 'Procesar Asientos Contables',
            html: `
                <ul style="text-align: left; margin-left: 20px;">
                    <li>Crear/actualizar liquidaciones de sueldo</li>
                    <li><strong>Devengado:</strong> ${MESES[mes - 1]} ${anio}</li>
                    <li><strong>Pago Remuneraciones:</strong> 5 de ${MESES[mesSig - 1]} ${anioSig}</li>
                    <li><strong>Pago Previred:</strong> 13 de ${MESES[mesSig - 1]} ${anioSig}</li>
                </ul>
            `,
            icon: 'question', showCancelButton: true, confirmButtonText: 'Si, procesar',
            cancelButtonText: 'Cancelar', confirmButtonColor: '#2bc046'
        });

        if (confirm.isConfirmed) {
            try {
                Swal.fire({ title: 'Procesando...', html: 'Generando liquidaciones y asientos contables', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                const res = await reprocesar(id);
                const prop = res.data?.data?.propagacion?.resultados || {};
                Swal.fire({
                    title: 'Procesamiento Completado',
                    html: `
                        <div style="text-align: left;">
                            <p><strong>Trabajadores procesados:</strong> ${prop.procesados || 0}</p>
                            <p><strong>Liquidaciones creadas:</strong> ${prop.liquidacionesCreadas || 0}</p>
                            <p><strong>Asientos de devengado:</strong> ${prop.asientosDevengadoGenerados || 0}</p>
                        </div>
                    `,
                    icon: 'success'
                });
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Error al procesar', 'error');
            }
        }
    };

    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            await cambiarEstadoPdf(id, nuevoEstado);
            Swal.fire('Exito', `Estado cambiado a ${nuevoEstado}`, 'success');
        } catch {
            Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
        }
    };

    // --- Render ---

    return (
        <div className="previred-container">
            {/* Header */}
            <div className="previred-header">
                <div>
                    <h1><FaFileAlt /> Previred</h1>
                    <p>Declaraciones mensuales de cotizaciones previsionales</p>
                </div>
            </div>

            {/* Paso 1: Selector de periodo */}
            <div className="periodo-selector">
                <div className="periodo-selector-label">
                    <FaCalendarAlt />
                    <span>Seleccionar periodo</span>
                </div>
                <div className="periodo-selector-controls">
                    <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
                        {MESES.map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <select value={anio} onChange={(e) => setAnio(parseInt(e.target.value))}>
                        {[2023, 2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Estado de carga */}
            {loading && <div className="loading">Cargando estado del periodo...</div>}
            {error && <div className="error-msg"><FaInfoCircle /> {error}</div>}

            {/* Panel de acciones del periodo */}
            {!loading && estado && (
                <div className="periodo-panel">
                    {/* Tarjeta: Descargar TXT */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaDownload />
                            <h3>Descargar TXT Previred</h3>
                        </div>
                        <div className="action-card-body">
                            {estado.tiene_liquidaciones ? (
                                <>
                                    <p className="action-info">{estado.cantidad_liquidaciones} liquidaciones encontradas para {MESES[mes - 1]} {anio}</p>
                                    <button className="btn-action btn-icon-tooltip btn-descargar" onClick={handleDescargarTxt} disabled={descargando} title={descargando ? 'Generando...' : 'Descargar TXT'}>
                                        <FaDownload /><span className="btn-tooltip-text">{descargando ? 'Generando...' : 'Descargar TXT'}</span>
                                    </button>
                                </>
                            ) : (
                                <p className="action-empty">No hay liquidaciones para este periodo. Debe crear las liquidaciones primero.</p>
                            )}
                        </div>
                    </div>

                    {/* Tarjeta: Cargar PDF pagado */}
                    <div className="action-card">
                        <div className="action-card-header">
                            <FaFilePdf />
                            <h3>Certificado PDF Pagado</h3>
                        </div>
                        <div className="action-card-body">
                            {!pdf ? (
                                <div className="upload-zone">
                                    <p className="action-info">Suba el certificado Previred pagado para extraer datos con IA</p>
                                    <div className="upload-form">
                                        <input
                                            type="file"
                                            id="pdf-input"
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                            disabled={subiendo}
                                        />
                                        {pdfFile && <span className="file-name">{pdfFile.name}</span>}
                                        <button
                                            className="btn-action btn-icon-tooltip btn-upload"
                                            onClick={handleSubirPdf}
                                            disabled={!pdfFile || subiendo}
                                            title={subiendo ? 'Procesando...' : 'Subir y Extraer'}
                                        >
                                            <FaCloudUploadAlt /><span className="btn-tooltip-text">{subiendo ? 'Procesando...' : 'Subir y Extraer'}</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="pdf-cargado">
                                    <div className="pdf-cargado-info">
                                        <span className={`badge badge-${pdf.estado?.toLowerCase()}`}>{pdf.estado}</span>
                                        <p><strong>Archivo:</strong> {pdf.nombre_archivo}</p>
                                        <p><strong>Empleador:</strong> {pdf.empleador_razon_social}</p>
                                        <p><strong>Trabajadores:</strong> {pdf.total_trabajadores}</p>
                                        <p><strong>Total Rem.:</strong> {formatCLP(pdf.total_remuneraciones)}</p>
                                    </div>
                                    <div className="pdf-cargado-actions">
                                        <button className="btn-action btn-icon-tooltip btn-ver-detalle" onClick={() => handleVerDetalle(pdf.id)} title="Ver Detalle">
                                            <FaEye /><span className="btn-tooltip-text">Ver Detalle</span>
                                        </button>
                                        {pdf.estado !== 'PROCESADO' && (
                                            <button className="btn-action btn-icon-tooltip btn-procesar" onClick={() => handleReprocesar(pdf.id)} title="Procesar Asientos">
                                                <FaCogs /><span className="btn-tooltip-text">Procesar Asientos</span>
                                            </button>
                                        )}
                                        {pdf.estado === 'PENDIENTE' && (
                                            <button className="btn-action btn-icon-tooltip btn-validar" onClick={() => handleCambiarEstado(pdf.id, 'VALIDADO')} title="Validar">
                                                <FaCheck /><span className="btn-tooltip-text">Validar</span>
                                            </button>
                                        )}
                                        <button className="btn-action btn-icon-tooltip btn-eliminar" onClick={() => handleEliminar(pdf.id)} title="Eliminar">
                                            <FaTrash /><span className="btn-tooltip-text">Eliminar</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de detalle */}
            {detalle && (
                <div className="modal-overlay" onClick={cerrarDetalle}>
                    <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Detalle - {detalle.cabecera?.periodo_mes}/{detalle.cabecera?.periodo_anio}</h2>
                        </div>

                        <div className="modal-info">
                            <p><strong>Empleador:</strong> {detalle.cabecera?.empleador_razon_social}</p>
                            <p><strong>RUT:</strong> {detalle.cabecera?.empleador_rut}</p>
                            <p><strong>AFPs:</strong> {detalle.afps?.join(', ')}</p>
                        </div>

                        <div className="modal-tabs">
                            {['AFP', 'ISL', 'SALUD'].map(tab => (
                                <button
                                    key={tab}
                                    className={activeTab === tab ? 'active' : ''}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab} ({detalle.detalles?.[tab]?.length || 0})
                                </button>
                            ))}
                        </div>

                        <div className="modal-body">
                            <table className="detalle-table">
                                <thead>
                                    <tr>
                                        <th>RUT</th>
                                        <th>Nombre</th>
                                        <th>Institucion</th>
                                        <th>Remuneracion</th>
                                        {activeTab === 'AFP' && <th>Cot. Obligatoria</th>}
                                        {activeTab === 'AFP' && <th>SIS</th>}
                                        {activeTab === 'AFP' && <th>Cesantia Afil.</th>}
                                        {activeTab === 'AFP' && <th>Cesantia Empl.</th>}
                                        {activeTab === 'ISL' && <th>Tasa</th>}
                                        {activeTab === 'ISL' && <th>Cotizacion ISL</th>}
                                        {activeTab === 'SALUD' && <th>Entidad</th>}
                                        {activeTab === 'SALUD' && <th>Cotizacion 7%</th>}
                                        <th>Mov.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalle.detalles?.[activeTab]?.map((det, idx) => (
                                        <tr key={idx}>
                                            <td>{det.rut_trabajador}</td>
                                            <td>{det.nombre_trabajador}</td>
                                            <td>{det.nombre_institucion}</td>
                                            <td>{formatCLP(det.remuneracion)}</td>
                                            {activeTab === 'AFP' && <td>{formatCLP(det.cotizacion_obligatoria)}</td>}
                                            {activeTab === 'AFP' && <td>{formatCLP(det.sis)}</td>}
                                            {activeTab === 'AFP' && <td>{formatCLP(det.cotizacion_afiliado_cesantia)}</td>}
                                            {activeTab === 'AFP' && <td>{formatCLP(det.cotizacion_empleador_cesantia)}</td>}
                                            {activeTab === 'ISL' && <td>{det.tasa_cotizacion_isl}%</td>}
                                            {activeTab === 'ISL' && <td>{formatCLP(det.cotizacion_isl)}</td>}
                                            {activeTab === 'SALUD' && <td>{det.entidad_salud}</td>}
                                            {activeTab === 'SALUD' && <td>{formatCLP(det.cotizacion_salud_7pct)}</td>}
                                            <td>{det.codigo_movimiento}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={cerrarDetalle}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreviredPage;
